import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { CalendarIcon, Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { ENTITIES as MASTER_ENTITIES } from '@/data/financial-systems';
import { useApp } from '@/contexts/AppContext';
import { getAllPurchaseTransactions } from '@/api/services/purchaseTransactionService';
import type { PurchaseTransactionApi } from '@/api/models/purchaseTransaction';

// Transform entities from master data
const ENTITIES = (MASTER_ENTITIES || []).map(entity => ({
  id: entity.id,
  name: entity.name + (entity.isHeadOffice ? ' (HQ)' : '')
}));

// Check if multi-entity system
const IS_MULTI_ENTITY = ENTITIES.length > 1;

// Cost categories
const COST_CATEGORIES = [
  { id: 'materials', name: 'Materials' },
  { id: 'equipment', name: 'Equipment' },
  { id: 'subcontractors', name: 'Subcontractors' },
  { id: 'utilities', name: 'Utilities' },
  { id: 'rent', name: 'Rent & Facilities' },
  { id: 'insurance', name: 'Insurance' },
  { id: 'marketing', name: 'Marketing & Advertising' },
  { id: 'software', name: 'Software & Subscriptions' },
  { id: 'travel', name: 'Travel & Entertainment' },
  { id: 'professional', name: 'Professional Services' },
  { id: 'other', name: 'Other Operating Costs' },
];

// Demo-mode cost transactions (supplier bills, expenses, cashbook)
const COST_TRANSACTIONS = [
  { id: 'bill-001', type: 'Supplier Bill', supplier: 'ABC Materials Ltd', description: 'Raw materials order', amount: 12500, dueDate: '2026-02-15' },
  { id: 'bill-002', type: 'Supplier Bill', supplier: 'Equipment Rentals Inc', description: 'Monthly equipment lease', amount: 8900, dueDate: '2026-02-20' },
  { id: 'exp-001', type: 'Expense', supplier: 'Tech Solutions Co', description: 'IT infrastructure upgrade', amount: 15000, dueDate: '2026-03-01' },
  { id: 'cb-001', type: 'Cashbook', supplier: 'Office Supplies Direct', description: 'Stationery and supplies', amount: 3200, dueDate: '2026-03-05' },
  { id: 'bill-003', type: 'Supplier Bill', supplier: 'Utilities Provider', description: 'Quarterly utility bill', amount: 5600, dueDate: '2026-03-10' },
];

// Transaction types for scenario transactions
const TRANSACTION_TYPES = [
  { id: 'new-supplier', name: 'New Supplier' },
  { id: 'one-off-expense', name: 'One-Off Expense' },
  { id: 'price-increase', name: 'Price Increase' },
  { id: 'other', name: 'Other' },
];

// Confidence levels
const CONFIDENCE_LEVELS = [
  { id: 'certain', name: 'Certain' },
  { id: 'probable', name: 'Probable' },
  { id: 'possible', name: 'Possible' },
  { id: 'unlikely', name: 'Unlikely' },
];

interface AdjustCostsByPercentFormProps {
  onSave: (data: any) => void;
  onCancel: () => void;
  onLivePreview?: (data: any) => void;
  initialData?: any;
}

export function AdjustCostsByPercentForm({ onSave, onCancel, onLivePreview, initialData }: AdjustCostsByPercentFormProps) {
  const isEditing = !!initialData;
  const [category, setCategory] = useState(
    initialData?.category
      ? (COST_CATEGORIES.find(c => c.name === initialData.category || c.id === initialData.category)?.id ?? '')
      : ''
  );
  const [adjustmentPercent, setAdjustmentPercent] = useState(initialData?.adjustmentPercent ?? 0);
  const [entity, setEntity] = useState<string>(
    initialData?.entity
      ? (ENTITIES.find(e => e.name === initialData.entity || e.id === initialData.entity)?.id ?? 'all')
      : 'all'
  );
  const [effectiveDate, setEffectiveDate] = useState<Date | undefined>(
    initialData?.effectiveDate ? new Date(initialData.effectiveDate) : undefined
  );
  const [reason, setReason] = useState(initialData?.reason ?? '');
  const [effectiveDateOpen, setEffectiveDateOpen] = useState(false);

  // Mock calculation - in real app, would calculate based on actual costs
  const estimatedCurrentCost = 45000; // £45K monthly for selected category
  const monthlyImpact = (estimatedCurrentCost * adjustmentPercent) / 100;
  const annualImpact = monthlyImpact * 12;

  // Update live preview whenever values change
  React.useEffect(() => {
    if (onLivePreview) {
      const selectedCategory = COST_CATEGORIES.find((c) => c.id === category);
      const selectedEntity = entity === 'all' ? null : ENTITIES.find((e) => e.id === entity);
      onLivePreview({
        type: 'adjust-costs-percent',
        category: selectedCategory?.name || '',
        adjustmentPercent,
        entity: entity === 'all' ? 'All Entities' : (selectedEntity?.name || 'All Entities'),
        effectiveDate,
        reason,
        monthlyImpact,
        annualImpact,
        estimatedCurrentCost,
      });
    }
  }, [category, adjustmentPercent, entity, effectiveDate, reason, monthlyImpact, annualImpact, estimatedCurrentCost, onLivePreview]);

  const handleSave = () => {
    const selectedCategory = COST_CATEGORIES.find((c) => c.id === category);
    const selectedEntity = entity === 'all' ? null : ENTITIES.find((e) => e.id === entity);

    onSave({
      type: 'adjust-costs-percent',
      category: selectedCategory?.name || '',
      adjustmentPercent,
      entity: entity === 'all' ? 'All Entities' : (selectedEntity?.name || 'All Entities'),
      effectiveDate,
      reason,
      monthlyImpact,
      annualImpact,
      estimatedCurrentCost,
    });
  };

  const isValid = category && adjustmentPercent !== 0 && effectiveDate;

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{isEditing ? 'Edit Cost Adjustment' : 'Adjust Costs by %'}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Category Selection */}
        <div className="space-y-2">
          <Label>Cost Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Select category..." />
            </SelectTrigger>
            <SelectContent>
              {COST_CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Adjustment Percentage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Cost Adjustment</Label>
            <span className="text-sm font-medium">
              {adjustmentPercent > 0 ? '+' : ''}{adjustmentPercent}%
            </span>
          </div>
          <Slider
            value={[adjustmentPercent]}
            onValueChange={(value) => setAdjustmentPercent(value[0])}
            min={-30}
            max={30}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>-30% (reduction)</span>
            <span>0%</span>
            <span>+30% (increase)</span>
          </div>
        </div>

        {/* Entity Filter - Conditional */}
        {IS_MULTI_ENTITY && (
          <div className="space-y-2">
            <Label>Entity</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {ENTITIES.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Effective Date */}
        <div className="space-y-2">
          <Label>Effective Date</Label>
          <Popover open={effectiveDateOpen} onOpenChange={setEffectiveDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !effectiveDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {effectiveDate ? format(effectiveDate, 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={effectiveDate}
                onSelect={(date) => {
                  setEffectiveDate(date);
                  setEffectiveDateOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Reason */}
        <div className="space-y-2">
          <Label>Reason (optional)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Cost reduction initiative, supplier negotiation..."
          />
        </div>

        {/* Live Impact Calculations */}
        {category && adjustmentPercent !== 0 && (
          <div className={cn(
            "p-4 border rounded-lg space-y-2",
            adjustmentPercent < 0 ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
          )}>
            <h4 className={cn(
              "font-semibold text-sm",
              adjustmentPercent < 0 ? "text-green-900" : "text-red-900"
            )}>
              {adjustmentPercent < 0 ? 'Savings Impact' : 'Cost Impact'}
            </h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Current Monthly Cost:</span>
                <span className="font-medium">£{estimatedCurrentCost.toLocaleString()}</span>
              </div>
              <div className={cn(
                "flex justify-between pt-2 border-t",
                adjustmentPercent < 0 ? "border-green-200" : "border-red-200"
              )}>
                <span className={cn(
                  "font-semibold",
                  adjustmentPercent < 0 ? "text-green-900" : "text-red-900"
                )}>
                  Monthly {adjustmentPercent < 0 ? 'Savings' : 'Increase'}:
                </span>
                <span className={cn(
                  "font-semibold",
                  adjustmentPercent < 0 ? "text-green-700" : "text-red-700"
                )}>
                  {adjustmentPercent < 0 ? '+' : '-'}£{Math.abs(Math.round(monthlyImpact)).toLocaleString()}/month
                </span>
              </div>
              <div className="flex justify-between">
                <span className={cn(
                  "font-semibold",
                  adjustmentPercent < 0 ? "text-green-900" : "text-red-900"
                )}>
                  Annual {adjustmentPercent < 0 ? 'Savings' : 'Increase'}:
                </span>
                <span className={cn(
                  "font-semibold",
                  adjustmentPercent < 0 ? "text-green-700" : "text-red-700"
                )}>
                  {adjustmentPercent < 0 ? '+' : '-'}£{Math.abs(Math.round(annualImpact)).toLocaleString()}/year
                </span>
              </div>
              {effectiveDate && (
                <div className={cn(
                  "pt-2 border-t",
                  adjustmentPercent < 0 ? "border-green-200" : "border-red-200"
                )}>
                  <p className={cn(
                    "text-xs",
                    adjustmentPercent < 0 ? "text-green-700" : "text-red-700"
                  )}>
                    💡 Impact starting {format(effectiveDate, 'MMMM yyyy')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isValid}>
          {isEditing ? 'Update Adjustment' : 'Add Adjustment'}
        </Button>
      </div>
    </div>
  );
}

interface AdjustSpecificTransactionFormProps {
  onSave: (data: any) => void;
  onCancel: () => void;
  onLivePreview?: (data: any) => void;
  initialData?: any;
}

export function AdjustSpecificTransactionForm({ onSave, onCancel, onLivePreview, initialData }: AdjustSpecificTransactionFormProps) {
  const isEditing = !!initialData;
  const { mode } = useApp();
  const [transactionId, setTransactionId] = useState(initialData?.transactionId ?? '');
  const [newDueDate, setNewDueDate] = useState<Date | undefined>(
    initialData?.newDueDate ? new Date(initialData.newDueDate) : undefined
  );
  const [adjustedValue, setAdjustedValue] = useState<number | undefined>(
    initialData?.adjustedValue !== undefined && initialData?.adjustedValue !== null
      ? Number(initialData.adjustedValue)
      : undefined
  );
  const [reason, setReason] = useState(initialData?.reason ?? '');
  const [confidence, setConfidence] = useState<'certain' | 'probable' | 'possible' | 'unlikely'>(initialData?.confidence ?? 'probable');
  const [newDueDateOpen, setNewDueDateOpen] = useState(false);
  const [liveTransactions, setLiveTransactions] = useState<typeof COST_TRANSACTIONS>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchTransactions = useCallback((search?: string) => {
    if (mode !== 'live') return;
    setIsLoading(true);
    getAllPurchaseTransactions({ outstandingOnly: true, limit: 100, search: search || undefined })
      .then((response) => {
        const mapped = response.purchaseTransactions.map((t: PurchaseTransactionApi) => ({
          id: String(t.id),
          type: t.transactionType || 'Supplier Bill',
          supplier: t.accountName || 'Unknown',
          description: t.transactionDescription || '',
          amount: t.gross ?? 0,
          dueDate: t.dueDate || t.anticipatedPaymentDate || t.transactionDate || '',
        }));
        setLiveTransactions(mapped);
      })
      .catch((err) => {
        console.error('Failed to fetch purchase transactions:', err);
        setLiveTransactions([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [mode]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    if (mode !== 'live') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchTransactions(value);
    }, 300);
  }, [mode, fetchTransactions]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const transactions = mode === 'live' ? liveTransactions : COST_TRANSACTIONS;

  useEffect(() => {
    if (initialData?.transactionId && transactions.length > 0) {
      const found = transactions.find(t => t.id === initialData.transactionId);
      if (!found) {
        console.warn('Edit mode: transactionId not found in transaction list', initialData.transactionId);
      }
    }
  }, [transactions, initialData]);

  const selectedTransaction = transactions.find((t) => t.id === transactionId);
  const originalDueDate = selectedTransaction ? new Date(selectedTransaction.dueDate) : null;
  
  // Calculate date change in days
  const dateChangeDays = originalDueDate && newDueDate 
    ? Math.round((newDueDate.getTime() - originalDueDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  // Calculate value change
  const valueChange = selectedTransaction && adjustedValue !== undefined
    ? adjustedValue - selectedTransaction.amount
    : 0;

  // Update live preview whenever values change
  React.useEffect(() => {
    if (onLivePreview && selectedTransaction && (newDueDate || adjustedValue !== undefined)) {
      onLivePreview({
        type: 'adjust-transaction',
        name: `Adjust ${selectedTransaction.supplier}`,
        transactionId: selectedTransaction.id,
        supplier: selectedTransaction.supplier,
        originalAmount: selectedTransaction.amount,
        amount: adjustedValue !== undefined ? adjustedValue : selectedTransaction.amount,
        adjustedValue: adjustedValue !== undefined ? adjustedValue : selectedTransaction.amount,
        dateChangeDays,
        valueChange,
        reason,
        confidence,
      });
    } else if (onLivePreview) {
      onLivePreview(null);
    }
  }, [transactionId, newDueDate, adjustedValue, dateChangeDays, valueChange, reason, confidence, selectedTransaction, onLivePreview]);

  const handleSave = () => {
    if (!selectedTransaction) return;

    onSave({
      type: 'adjust-transaction',
      name: `Adjust ${selectedTransaction.supplier}`,
      transactionId: selectedTransaction.id,
      transactionType: selectedTransaction.type,
      supplier: selectedTransaction.supplier,
      originalAmount: selectedTransaction.amount,
      amount: adjustedValue !== undefined ? adjustedValue : selectedTransaction.amount,
      originalDueDate: selectedTransaction.dueDate,
      newDueDate: newDueDate || new Date(selectedTransaction.dueDate),
      adjustedValue: adjustedValue !== undefined ? adjustedValue : selectedTransaction.amount,
      dateChangeDays,
      valueChange,
      reason,
      confidence,
    });
  };

  const isValid = transactionId && (newDueDate || adjustedValue !== undefined);

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{isEditing ? 'Edit Transaction Adjustment' : 'Adjust Specific Transaction'}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Transaction Selection */}
        <div className="space-y-2">
          <Label>Transaction</Label>
          <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={comboboxOpen}
                className="w-full justify-between font-normal"
              >
                {transactionId
                  ? (() => {
                      const txn = transactions.find((t) => t.id === transactionId);
                      return txn
                        ? `${txn.type} - ${txn.supplier}${txn.description ? ` - ${txn.description}` : ''} - £${txn.amount.toLocaleString()}`
                        : 'Select transaction...';
                    })()
                  : 'Select transaction...'}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Type to search transactions..."
                  value={searchTerm}
                  onValueChange={handleSearchChange}
                />
                <CommandList>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-sm text-muted-foreground">Loading transactions...</span>
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No transactions available
                    </div>
                  ) : (
                    <>
                      <CommandEmpty>No transactions found.</CommandEmpty>
                      <CommandGroup>
                        {transactions.map((txn) => (
                          <CommandItem
                            key={txn.id}
                            value={txn.id}
                            onSelect={(value) => {
                              setTransactionId(value === transactionId ? '' : value);
                              setComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                transactionId === txn.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {txn.type} - {txn.supplier}{txn.description ? ` - ${txn.description}` : ''} - £{txn.amount.toLocaleString()} (Due: {format(new Date(txn.dueDate), 'MMM dd')})
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Show transaction details when selected */}
        {selectedTransaction && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type:</span>
              <span className="font-medium">{selectedTransaction.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Supplier:</span>
              <span className="font-medium">{selectedTransaction.supplier}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Amount:</span>
              <span className="font-medium">£{selectedTransaction.amount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Due Date:</span>
              <span className="font-medium">{format(new Date(selectedTransaction.dueDate), 'MMM dd, yyyy')}</span>
            </div>
          </div>
        )}

        {/* New Due Date (Optional) */}
        <div className="space-y-2">
          <Label>New Due Date (optional)</Label>
          <Popover open={newDueDateOpen} onOpenChange={setNewDueDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !newDueDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {newDueDate ? format(newDueDate, 'PPP') : 'Keep current date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={newDueDate}
                onSelect={(date) => {
                  setNewDueDate(date);
                  setNewDueDateOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Adjusted Value (Optional) */}
        <div className="space-y-2">
          <Label>Adjusted Value (optional)</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm">£</span>
            <Input
              type="number"
              value={adjustedValue !== undefined ? adjustedValue : ''}
              onChange={(e) => setAdjustedValue(e.target.value ? Number(e.target.value) : undefined)}
              placeholder={selectedTransaction ? `Keep current (£${selectedTransaction.amount.toLocaleString()})` : 'Keep current value'}
              className="flex-1"
            />
          </div>
        </div>

        {/* Reason */}
        <div className="space-y-2">
          <Label>Reason (optional)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Negotiated payment terms, supplier discount..."
          />
        </div>

        {/* Confidence */}
        <div className="space-y-2">
          <Label>Confidence Level</Label>
          <Select value={confidence} onValueChange={(v) => setConfidence(v as typeof confidence)}>
            <SelectTrigger>
              <SelectValue placeholder="Select confidence..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="certain">Certain</SelectItem>
              <SelectItem value="probable">Probable</SelectItem>
              <SelectItem value="possible">Possible</SelectItem>
              <SelectItem value="unlikely">Unlikely</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            How confident are you this adjustment will occur?
          </p>
        </div>

        {/* Live Impact Calculations */}
        {selectedTransaction && (newDueDate || adjustedValue !== undefined) && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
            <h4 className="font-semibold text-sm text-blue-900">Transaction Impact</h4>
            <div className="space-y-1 text-sm">
              {newDueDate && dateChangeDays !== 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date Change:</span>
                    <span className={cn(
                      "font-medium",
                      dateChangeDays > 0 ? "text-blue-700" : "text-orange-700"
                    )}>
                      {dateChangeDays > 0 ? '+' : ''}{dateChangeDays} days ({dateChangeDays > 0 ? 'delayed' : 'earlier'})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">New Date:</span>
                    <span className="font-medium">{format(newDueDate, 'MMM dd, yyyy')}</span>
                  </div>
                </>
              )}
              {adjustedValue !== undefined && valueChange !== 0 && (
                <>
                  <div className={cn(
                    "flex justify-between pt-2",
                    newDueDate && dateChangeDays !== 0 ? "border-t border-blue-200" : ""
                  )}>
                    <span className="text-muted-foreground">Value Change:</span>
                    <span className={cn(
                      "font-medium",
                      valueChange < 0 ? "text-green-700" : "text-red-700"
                    )}>
                      {valueChange > 0 ? '+' : ''}£{valueChange.toLocaleString()} ({valueChange < 0 ? 'reduction' : 'increase'})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">New Value:</span>
                    <span className="font-medium">£{adjustedValue.toLocaleString()}</span>
                  </div>
                </>
              )}
              {newDueDate && dateChangeDays > 0 && (
                <div className="pt-2 border-t border-blue-200">
                  <p className="text-xs text-blue-700">
                    💡 Cash preserved: £{selectedTransaction.amount.toLocaleString()} from{' '}
                    {format(new Date(selectedTransaction.dueDate), 'MMM yyyy')} to{' '}
                    {format(newDueDate, 'MMM yyyy')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isValid}>
          {isEditing ? 'Update Adjustment' : 'Add Adjustment'}
        </Button>
      </div>
    </div>
  );
}

interface AddScenarioTransactionFormProps {
  onSave: (data: any) => void;
  onCancel: () => void;
  onLivePreview?: (data: any) => void;
  initialData?: any;
}

export function AddScenarioTransactionForm({ onSave, onCancel, onLivePreview, initialData }: AddScenarioTransactionFormProps) {
  const isEditing = !!initialData;
  const [transactionType, setTransactionType] = useState(
    initialData?.transactionType
      ? (TRANSACTION_TYPES.find(t => t.name === initialData.transactionType || t.id === initialData.transactionType)?.id ?? '')
      : ''
  );
  const [expectedDate, setExpectedDate] = useState<Date | undefined>(
    initialData?.expectedDate ? new Date(initialData.expectedDate) : undefined
  );
  const [amount, setAmount] = useState<number>(initialData?.amount ?? 0);
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [confidence, setConfidence] = useState<'certain' | 'probable' | 'possible' | 'unlikely'>(
    initialData?.confidence
      ? (CONFIDENCE_LEVELS.find(c => c.name === initialData.confidence || c.id === initialData.confidence)?.id as 'certain' | 'probable' | 'possible' | 'unlikely' ?? 'probable')
      : 'probable'
  );
  const [businessContext, setBusinessContext] = useState(initialData?.businessContext ?? '');
  const [expectedDateOpen, setExpectedDateOpen] = useState(false);

  // Update live preview whenever values change
  React.useEffect(() => {
    if (onLivePreview && amount > 0 && expectedDate) {
      onLivePreview({
        type: 'add-scenario-transaction',
        transactionType,
        expectedDate,
        amount,
        description,
        confidence,
        businessContext,
      });
    } else if (onLivePreview) {
      onLivePreview(null);
    }
  }, [transactionType, expectedDate, amount, description, confidence, businessContext, onLivePreview]);

  const handleSave = () => {
    const selectedType = TRANSACTION_TYPES.find((t) => t.id === transactionType);
    const selectedConfidence = CONFIDENCE_LEVELS.find((c) => c.id === confidence);

    onSave({
      type: 'add-scenario-transaction',
      transactionType: selectedType?.name || '',
      expectedDate,
      amount,
      description,
      confidence: selectedConfidence?.name || '',
      businessContext,
    });
  };

  const isValid = transactionType && expectedDate && amount > 0 && description && confidence;

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{isEditing ? 'Edit Scenario Transaction' : 'Add Scenario Transaction'}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Transaction Type */}
        <div className="space-y-2">
          <Label>Transaction Type</Label>
          <Select value={transactionType} onValueChange={setTransactionType}>
            <SelectTrigger>
              <SelectValue placeholder="Select type..." />
            </SelectTrigger>
            <SelectContent>
              {TRANSACTION_TYPES.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Expected Date */}
        <div className="space-y-2">
          <Label>Expected Date</Label>
          <Popover open={expectedDateOpen} onOpenChange={setExpectedDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !expectedDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {expectedDate ? format(expectedDate, 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={expectedDate}
                onSelect={(date) => {
                  setExpectedDate(date);
                  setExpectedDateOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Amount */}
        <div className="space-y-2">
          <Label>Amount</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm">£</span>
            <Input
              type="number"
              value={amount || ''}
              onChange={(e) => setAmount(Number(e.target.value))}
              placeholder="0"
              className="flex-1"
            />
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Anticipated equipment repair, new supplier contract..."
            rows={2}
          />
        </div>

        {/* Confidence Level */}
        <div className="space-y-2">
          <Label>Confidence Level</Label>
          <Select value={confidence} onValueChange={(v) => setConfidence(v as typeof confidence)}>
            <SelectTrigger>
              <SelectValue placeholder="Select confidence..." />
            </SelectTrigger>
            <SelectContent>
              {CONFIDENCE_LEVELS.map((level) => (
                <SelectItem key={level.id} value={level.id}>
                  {level.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Business Context */}
        <div className="space-y-2">
          <Label>Business Context (optional)</Label>
          <Textarea
            value={businessContext}
            onChange={(e) => setBusinessContext(e.target.value)}
            placeholder="e.g., Why is this cost anticipated? What's the business driver?"
            rows={2}
          />
        </div>

        {/* Live Impact Calculations */}
        {amount > 0 && expectedDate && (
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-2">
            <h4 className="font-semibold text-sm text-orange-900">Cost Impact</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount:</span>
                <span className="font-medium text-orange-700">
                  -£{amount.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Expected:</span>
                <span className="font-medium">{format(expectedDate, 'MMMM yyyy')}</span>
              </div>
              {confidence && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Confidence:</span>
                  <span className="font-medium">
                    {CONFIDENCE_LEVELS.find((c) => c.id === confidence)?.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isValid}>
          {isEditing ? 'Update Adjustment' : 'Add Adjustment'}
        </Button>
      </div>
    </div>
  );
}
